class ChatbotAPI {
  /**
   * @param {string} baseUrl - L'URL de base pour les appels API (optionnel)
   */
  constructor(baseUrl = null) {
    this.baseUrl = baseUrl || window.location.origin;
  }

  /**
   * Méthode générique pour envoyer une requête à l'API du chatbot
   * @param {string} endpoint - Le endpoint de l'API
   * @param {string} method - La méthode HTTP (GET, POST, etc.)
   * @param {object} data - Les données à envoyer
   * @param {object} headers - Les headers à inclure
   * @returns {Promise} - Une promesse avec la réponse de l'API
   */
  async callAPI(endpoint, method = "GET", data = null, headers = {}) {
    try {
      // Construction de l'URL complète
      const url = `${this.baseUrl}${endpoint}`;

      // Configuration des options de la requête
      const options = {
        method: method,
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
      };

      // Ajout des données si nécessaire
      if (data) {
        options.body = JSON.stringify(data);
      }

      // Envoi de la requête
      const response = await fetch(url, options);

      // Vérification de la réponse
      if (!response.ok) {
        throw new Error(`Erreur HTTP statut: ${response.status}`);
      }

      // Retour des données
      return await response.json();
    } catch (error) {
      console.error("Erreur lors de l'appel à l'API:", error);
      throw error;
    }
  }

  /**
   * Méthode pour envoyer un message au chatbot et recevoir un stream de réponse
   * @param {string} message - Le message à envoyer
   * @param {function} callback - La fonction de rappel pour traiter les chunks de réponse
   * @returns {Promise} - Une promesse avec la réponse complète du chatbot
   */
  sendMessageStream(
    message,
    roomId = null,
    handlers = {
      onStart: () => {},
      onFirstChunk: () => {},
      onAssistantMessage: () => {},
      onPeerSupportMessage: () => {},
      onSourceMessage: () => {},
      onToolMessage: () => {},
      onErrorMessage: () => {},
      onDone: () => {},
      onErreur: () => {},
    },
  ) {
    let url = `${this.baseUrl}/api/chatbot/stream?message=${encodeURIComponent(message)}`;

    if (roomId) {
      url += `&room_id=${encodeURIComponent(roomId)}`;
    }

    let first = true;
    const eventSource = new EventSource(url);

    eventSource.addEventListener("start", (event) => {
      const data = JSON.parse(event.data);
      handlers.onStart?.(data);
    });

    eventSource.addEventListener("assistantMessage", (event) => {
      if (first) {
        handlers.onFirstChunk?.();
        first = false;
      }
      handlers.onAssistantMessage?.(JSON.parse(event.data));
    });

    eventSource.addEventListener("peerSupportMessage", (event) => {
      handlers.onPeerSupportMessage?.(JSON.parse(event.data));
    });

    eventSource.addEventListener("sourceMessage", (event) => {
      handlers.onSourceMessage?.(JSON.parse(event.data));
    });

    eventSource.addEventListener("toolMessage", (event) => {
      handlers.onToolMessage?.(JSON.parse(event.data));
    });

    eventSource.addEventListener("errorMessage", (event) => {
      handlers.onErrorMessage?.(JSON.parse(event.data));
    });

    eventSource.addEventListener("done", (event) => {
      eventSource.close();
      handlers.onDone?.(JSON.parse(event.data));
    });

    eventSource.addEventListener("error", (err) => {
      try {
        const anyTarget = err;
        if (anyTarget && typeof anyTarget.data === "string") {
          try {
            const parsed = JSON.parse(anyTarget.data);
            console.error(parsed);
          } catch {
            console.error(anyTarget.data);
          }
        } else {
          console.error(err);
        }
      } finally {
        eventSource.close();
      }
    });

    return eventSource;
  }
}

export default ChatbotAPI;
